<script lang="ts">
  // libs
  import { onMount } from "svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // components
  import EmptyPaymentMethod from "@retailer/svelte/Payments/EmptyPaymentMethod.svelte";
  import InvoicesPaymentMethods from "@retailer/svelte/Payments/Settings/InvoicesPaymentMethods.svelte";
  import FAQ from "@shared/svelte/Components/FAQ.svelte";
  import AutoPay from "@retailer/svelte/Payments/Settings/AutoPay.svelte";
  import Flash from "@shared/services/flashv2.js";
  import PaymentSettingsBannerSelector from "@retailer/svelte/Payments/Banners/selectors/PaymentSettingsBannerSelector.svelte";

  // services
  import * as AutoPaySettingsService from "@retailer/services/auto_pay_settings_service";
  import * as PaymentInstrumentService from "@retailer/services/payment_instrument_service";
  import * as BankAccountService from "@retailer/services/bank_account_service";
  import * as DwollaIdentityService from "@retailer/services/dwolla_identity_service";

  // stores
  import { autoPaySettingsStore } from "@retailer/svelte/stores/AutoPaySettingsStore";
  import {
    paymentInstrumentsStore,
    pendingPaymentInstruments,
  } from "@retailer/svelte/stores/PaymentInstrumentStore";
  import { bankAccount as bankAccountStore } from "@retailer/stores/BankAccountStore";
  import { dwollaIdentityStore } from "@retailer/svelte/stores/DwollaIdentityStore";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import { authorizationCheck } from "@shared/svelte/Modules/AuthorizationCheck.svelte";

  export let updateShowUnsavedChangesAlert: (newValue: boolean) => void;

  // constants
  let specsReadOnly = stagedRolloutCheck("specs_read_only_payments");
  let showModals = {
    AddBankModal: false,
    ChoosePaymentTypeModal: false,
    ShowInstrumentMenu: false,
    PaymentMethodUpdateModal: false,
  };

  function onCloseModals() {
    Object.entries(showModals).forEach(function (entry) {
      showModals[entry[0]] = false;
    });
  }

  async function afterSave() {
    const hasPreviousInstruments = $paymentInstrumentsStore.paymentInstruments.length != 0;

    await PaymentInstrumentService.get();

    onCloseModals();

    let message = "Your payment method was successfully saved.";
    if (!hasPreviousInstruments) {
      message += ` <a class="underline" href="/settings/employees">Review employee permissions.</a>`;
    }

    Flash.now({
      type: "success",
      rawHtml: !hasPreviousInstruments,
      message,
    });
  }

  async function afterSuccess() {
    await PaymentInstrumentService.get();
    await AutoPaySettingsService.get();
  }

  const afterMicrodepositFailure = () => {
    Flash.now({
      type: "error",
      header: "There was an error verifying your bank account",
      message: "Please try again or add a different payment method.",
    });
  };

  const rerouteIfReadOnly = () => {
    const invoicesTabUrl = "/invoices";
    if (specsReadOnly || !authorizationCheck("payment_instruments.index")) {
      window.location.assign(invoicesTabUrl);
    }
  };

  rerouteIfReadOnly();

  $: verifiedPaymentMethodsExist = $paymentInstrumentsStore.paymentInstruments.length > 0;
  $: paymentMethodsExist = verifiedPaymentMethodsExist || $pendingPaymentInstruments.length > 0;

  onMount(() => {
    (async () => {
      await PaymentInstrumentService.get();
      await AutoPaySettingsService.get();
      await BankAccountService.get();
      await DwollaIdentityService.get();
    })();
  });

  $: innerWidth = 0;
  // force the first value for viewport
  let viewportIsSmall: boolean = innerWidth && innerWidth <= 700;
  $: viewportIsSmall = innerWidth && innerWidth <= 700;
  $: isLoaded =
    $paymentInstrumentsStore.loaded &&
    $autoPaySettingsStore.loaded &&
    $bankAccountStore.loaded &&
    $dwollaIdentityStore.loaded;
</script>

<svelte:window bind:innerWidth />
<Authorization authName="payment_instruments.index">
  {#if isLoaded}
    <PaymentSettingsBannerSelector />
    <div data-testid="payment-settings">
      {#if paymentMethodsExist}
        <div class="fizz-space-between-responsive" data-testid="payment-methods-container">
          <div
            class="fizz-align-self-start fizz-flex-vertical fizz-bordered fizz-inset-square-24"
            class:fizz-half-width={!viewportIsSmall}
          >
            <InvoicesPaymentMethods
              bind:showModals
              {onCloseModals}
              afterInstrumentAddSuccess={afterSave}
              {afterSuccess}
              {afterMicrodepositFailure}
            />
          </div>
          {#if verifiedPaymentMethodsExist}
            <div
              class="fizz-bordered fizz-inset-square-24 fizz-stack-32"
              class:fizz-half-width={!viewportIsSmall}
            >
              <AutoPay {updateShowUnsavedChangesAlert} />
            </div>
          {/if}
        </div>
      {:else}
        <EmptyPaymentMethod afterInstrumentAddSuccess={afterSave} />
      {/if}
      <FAQ url={"https://support.provi.com/s/article/how-does-provi-pay"} />
    </div>
  {:else}
    <div class="fizz-flex-row fizz-justify-center">
      <div class="fizz-icon-spinner-small" data-testid="loading-spinner" />
    </div>
  {/if}
</Authorization>
